import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, Calendar, User, Quote, Dumbbell, Heart, Target, Flame } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import CTACard from "@/components/blog/CTACard";
import BlogImage from "@/components/blog/BlogImage";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { getPostBySlug } from "@/data/blogPosts";
const HabitoExercicios = () => {
  const post = getPostBySlug("como-criar-o-habito-de-exercicios") || {
    metaTitle: "Como Criar o Hábito de Exercícios Físicos: Guia Completo | HabitFrog",
    metaDescription: "Aprenda como criar o hábito de exercícios físicos de forma definitiva. Dicas de atletas, história real de superação e estratégias comprovadas para nunca mais abandonar o treino.",
    category: "Fitness",
    author: "Equipe HabitFrog",
    date: "15 Dez 2025",
    readTime: "20 min de leitura",
    slug: "como-criar-o-habito-de-exercicios",
    title: "Como Criar o Hábito de Exercícios"
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.metaTitle} />
        <meta name="twitter:description" content={post.metaDescription} />
        <link rel="canonical" href={`https://habitfrog.com/blog/${post.slug}`} />
        <meta name="keywords" content="como criar hábito de exercícios, hábito de exercícios físicos, criar rotina de treino, manter consistência academia, começar a treinar, voltar a fazer exercício" />
      </Helmet>

      <Header />
      <ScrollToTopButton />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <nav className="mb-8" aria-label="Breadcrumb">
            <Link to="/home" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao início
            </Link>
          </nav>

          <header className="max-w-4xl mx-auto mb-12">
            <span className="inline-block bg-coral/20 text-coral px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {post.category}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 leading-tight">
              Como Criar o Hábito de Exercícios Físicos: O Guia Definitivo
            </h1>
            <p className="text-xl text-muted-foreground mb-6">
              Um guia completo com histórias reais, ciência do comportamento e estratégias de atletas de elite para você nunca mais abandonar o treino
            </p>
            <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{post.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
            </div>
          </header>

          <article className="max-w-4xl mx-auto prose prose-lg max-w-none">
            {/* Introdução */}
            <div className="bg-gradient-to-r from-coral/10 via-primary/10 to-accent/20 p-8 rounded-2xl border mb-12">
              <div className="flex items-start gap-4">
                <Dumbbell className="w-8 h-8 text-coral flex-shrink-0 mt-1" />
                <div>
                  <p className="text-xl text-foreground leading-relaxed mb-4">
                    Você já começou a fazer exercícios físicos com toda a motivação do mundo, só para abandonar semanas depois? Já prometeu a si mesmo que "dessa vez vai ser diferente", comprou roupas de academia, fez matrícula, e mesmo assim não conseguiu manter o hábito de exercícios? Se você se identificou, saiba que você não está sozinho – e que existe uma forma comprovada de mudar isso.
                  </p>
                  <p className="text-muted-foreground">
                    Neste guia completo, vamos explorar como criar o hábito de exercícios físicos de forma definitiva, com base em ciência, histórias reais de superação e estratégias usadas por atletas de alto desempenho.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Criar o hábito de exercícios não é sobre força de vontade ou disciplina sobre-humana. É sobre entender como seu cérebro funciona, criar o ambiente certo e usar estratégias inteligentes que transformam o esforço consciente em comportamento automático. Vamos descobrir juntos como fazer isso funcionar para você.
              </p>
            </div>

            <BlogImage 
              src="/images/blog/corrida-atleta.jpg" 
              alt="Pessoa correndo ao ar livre ao amanhecer, representando o início da jornada para criar o hábito de exercícios físicos e transformar a saúde" 
              className="h-64 md:h-80 mb-12"
              priority
            />

            {/* A História de Maria */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Heart className="w-10 h-10 text-coral" />
                A História de Maria: Quando o Amor Atrapalhou o Hábito de Exercícios
              </h2>

              <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/20 p-8 rounded-2xl border mb-8">
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xl text-foreground italic leading-relaxed mb-4">
                      "Eu sempre fui aquela menina que acordava cedo para correr, que amava sentir o corpo em movimento. Quando casei, achei que seria ainda melhor – finalmente teria um parceiro de treino. Três anos depois, eu mal conseguia subir escadas sem ficar ofegante. O exercício que eu esperava fazer 'junto' nunca aconteceu."
                    </p>
                    <p className="text-muted-foreground font-semibold">— Maria, 24 anos, São Paulo</p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">O Hábito de Exercícios Antes do Casamento</h3>
                  <p className="text-foreground/80 mb-4">
                    Maria sempre foi apaixonada por exercícios físicos. Desde os 16 anos, mantinha uma rotina consistente: corrida três vezes por semana, musculação outras três, e yoga aos domingos. Para ela, exercitar-se era tão natural quanto escovar os dentes.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    "Meus amigos brincavam que eu era viciada em endorfina. E acho que era mesmo. Eu acordava às 5h30 sem despertador, meu corpo já sabia que era hora de treinar. Era o meu momento, minha terapia, minha forma de começar o dia com energia."
                  </p>
                  <p className="text-foreground/80">
                    Quando conheceu Pedro, aos 21 anos, Maria imaginou que tinha encontrado o parceiro perfeito – ele também dizia gostar de exercícios e prometia que iriam treinar juntos quando casassem.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Como Criar o Hábito de Exercícios se Desfez</h3>
                  <p className="text-foreground/80 mb-4">
                    Depois do casamento, Maria esperava ansiosamente começar a rotina de treinos com o marido. Mas Pedro sempre tinha uma desculpa: estava cansado, tinha trabalho, amanhã seria melhor. Maria, querendo fazer junto, começou a esperar.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    "No começo, eu pensava: 'tudo bem, semana que vem a gente começa'. Semanas viraram meses, meses viraram anos. Sem perceber, eu tinha abandonado completamente o hábito de exercícios que sempre me definiu."
                  </p>
                  <p className="text-foreground/80">
                    O corpo de Maria começou a dar sinais: dores nas costas, falta de ar, cansaço constante, dificuldade para dormir. A mobilidade que ela tinha aos 21 anos havia desaparecido aos 24.
                  </p>
                </div>
              </div>

              <BlogImage 
                src="/images/blog/mulher-alongamento.jpg" 
                alt="Mulher jovem fazendo alongamento e exercícios, representando a reconquista do hábito de exercícios físicos" 
                className="h-64 md:h-80 mb-8"
              />

              <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral mb-8">
                <h3 className="font-bold text-foreground mb-2">O Ponto de Virada</h3>
                <p className="text-foreground/80">
                  "Um dia, fui brincar com minha sobrinha de 5 anos no parque. Em 10 minutos, eu estava completamente exausta, sem fôlego, com as pernas tremendo. Uma criança de 5 anos tinha mais condicionamento físico que eu. Foi humilhante e assustador. Naquele momento, percebi que não podia mais esperar por ninguém. Eu precisava criar o hábito de exercícios de novo – dessa vez, por mim mesma."
                </p>
              </div>
            </section>

            {/* A Decisão de Voltar */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Target className="w-10 h-10 text-primary" />
                Como Criar o Hábito de Exercícios: A Jornada de Volta de Maria
              </h2>

              <p className="text-foreground/80 mb-8">
                Maria decidiu que precisava reconstruir seu hábito de exercícios físicos do zero. Mas dessa vez, ela fez diferente – pesquisou sobre formação de hábitos, consultou profissionais e aplicou estratégias que realmente funcionam.
              </p>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">🎯</div>
                  <h3 className="font-semibold text-foreground mb-3">Semana 1-2: Começando Pequeno</h3>
                  <p className="text-sm text-muted-foreground">
                    Maria não voltou tentando treinar uma hora por dia. Começou com apenas 10 minutos de caminhada. "Eu sabia que se tentasse fazer muito, desistiria de novo. O importante era criar o hábito de exercícios, não o treino perfeito."
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">⏰</div>
                  <h3 className="font-semibold text-foreground mb-3">Semana 3-4: Estabelecendo Horário</h3>
                  <p className="text-sm text-muted-foreground">
                    Definiu um horário fixo: 6h da manhã, antes de tudo. "Se eu deixasse para depois do trabalho, sempre aparecia uma desculpa. Treinar cedo virou minha âncora para criar o hábito de exercícios físicos."
                  </p>
                </div>
                <div className="bg-card p-6 rounded-xl border">
                  <div className="text-4xl mb-4">📈</div>
                  <h3 className="font-semibold text-foreground mb-3">Mês 2-3: Aumentando Gradualmente</h3>
                  <p className="text-sm text-muted-foreground">
                    De 10 minutos, passou para 20, depois 30. Adicionou exercícios de mobilidade, depois força. "A cada semana, eu me sentia mais capaz. Meu corpo estava reaprendendo a se mover."
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">Os Resultados de Criar o Hábito de Exercícios</h3>
                  <p className="text-foreground/80 mb-4">
                    Após 6 meses consistentes, Maria recuperou muito do que havia perdido – e ganhou ainda mais. A mobilidade voltou, as dores nas costas sumiram, o sono melhorou drasticamente.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    "Hoje eu corro 5km sem parar, faço agachamento com carga, e consigo brincar com minha sobrinha por horas. Mas o mais importante não é o físico – é como me sinto. Tenho energia, disposição, e uma confiança que havia perdido."
                  </p>
                  <p className="text-foreground/80">
                    <strong>E o marido?</strong> "Pedro viu minha transformação e, ironicamente, agora ele quer treinar comigo. Mas eu não espero mais por ele. Tenho meu horário, minha rotina. Se ele quiser participar, ótimo. Se não, eu vou mesmo assim."
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/treino-forca.jpg" 
                  alt="Pessoa sorrindo após treino de força na academia, representando a conquista de criar o hábito de exercícios físicos" 
                  className="h-64"
                />
              </div>
            </section>

            <div className="my-16"><CTACard variant="primary" /></div>

            {/* Lições de Atletas de Elite */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6 flex items-center gap-4">
                <Flame className="w-10 h-10 text-coral" />
                O Que Atletas de Elite Ensinam Sobre Criar o Hábito de Exercícios
              </h2>

              <p className="text-foreground/80 mb-8">
                Se você quer criar o hábito de exercícios físicos de forma definitiva, não há melhor fonte de inspiração do que aqueles que dedicam suas vidas ao treinamento. Vejamos o que podemos aprender com alguns dos maiores atletas do mundo.
              </p>

              {/* Arnold Schwarzenegger */}
              <div className="bg-gradient-to-r from-card to-accent/10 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/treino-forca.jpg" 
                      alt="Atleta treinando com pesos, representando disciplina e consistência para criar hábito de exercícios físicos" 
                      className="h-48"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Arnold Schwarzenegger: A Visão Clara</h3>
                    <p className="text-foreground/80 mb-4">
                      O lendário fisiculturista, sete vezes Mr. Olympia, sempre enfatizou a importância de ter uma visão clara do objetivo. Arnold visualizava exatamente como queria que seu corpo fosse – e essa imagem mental o impulsionava a treinar mesmo nos dias mais difíceis.
                    </p>
                    <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "A mente sempre desiste antes do corpo. Você precisa convencer sua mente de que você pode fazer isso – então o corpo seguirá."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Arnold Schwarzenegger</p>
                    </div>
                    <p className="text-foreground/80 mt-4">
                      <strong>Lição para criar o hábito de exercícios:</strong> Tenha uma visão clara de por que você quer se exercitar. Não basta "querer emagrecer" – visualize como você quer se sentir, o que quer ser capaz de fazer, como quer envelhecer.
                    </p>
                  </div>
                </div>
              </div>

              {/* Cristiano Ronaldo */}
              <div className="bg-gradient-to-r from-card to-primary/10 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Cristiano Ronaldo: A Consistência Acima de Tudo</h3>
                    <p className="text-foreground/80 mb-4">
                      CR7 é conhecido por sua obsessão com a consistência. Aos 38 anos, ainda compete no mais alto nível porque nunca para de treinar – mesmo nas férias, mesmo quando está lesionado (adaptando os exercícios), mesmo quando não está "no clima".
                    </p>
                    <p className="text-foreground/80 mb-4">
                      Relatos de colegas de time dizem que Ronaldo é sempre o primeiro a chegar e o último a sair do centro de treinamento. Ele faz crioterapia, dorme de 8 a 10 horas por noite em ciclos específicos, e segue uma dieta rigorosa 365 dias por ano.
                    </p>
                    <div className="bg-coral/10 p-4 rounded-lg border border-coral/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "Talento sem trabalho duro é nada. O trabalho duro vence o talento quando o talento não trabalha duro."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Cristiano Ronaldo</p>
                    </div>
                    <p className="text-foreground/80 mt-4">
                      <strong>Lição para criar o hábito de exercícios físicos:</strong> Não espere motivação. Apareça todos os dias, mesmo quando não está com vontade. É a consistência, não a intensidade ocasional, que cria resultados duradouros.
                    </p>
                  </div>
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/corrida-atleta.jpg" 
                      alt="Atleta em treinamento intenso, representando consistência para criar hábito de exercícios físicos" 
                      className="h-48"
                    />
                  </div>
                </div>
              </div>

              {/* Serena Williams */}
              <div className="bg-gradient-to-r from-card to-golden/10 p-8 rounded-2xl border mb-8">
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="md:col-span-1">
                    <BlogImage 
                      src="/images/blog/mulher-alongamento.jpg" 
                      alt="Atleta profissional em ação, representando resiliência no hábito de exercícios físicos" 
                      className="h-48"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <h3 className="text-2xl font-bold text-foreground mb-4">Serena Williams: O Poder da Identidade</h3>
                    <p className="text-foreground/80 mb-4">
                      Uma das maiores tenistas de todos os tempos, Serena sempre se identificou como atleta antes de qualquer outra coisa. Mesmo após se tornar mãe, mesmo após lesões graves, ela nunca abandonou essa identidade – e é isso que a fez voltar mais forte.
                    </p>
                    <p className="text-foreground/80 mb-4">
                      Serena treinou durante a gravidez (com adaptações), voltou às quadras meses após o parto, e continuou competindo em alto nível por anos. Ela não "faz exercícios" – ela É uma atleta.
                    </p>
                    <div className="bg-golden/10 p-4 rounded-lg border border-golden/30">
                      <p className="text-sm font-medium text-foreground italic">
                        "Eu não me vejo como uma pessoa que treina. Eu me vejo como uma atleta. Treinar é o que atletas fazem. É quem eu sou."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Serena Williams</p>
                    </div>
                    <p className="text-foreground/80 mt-4">
                      <strong>Lição para criar o hábito de exercícios:</strong> Mude sua identidade. Em vez de dizer "estou tentando fazer exercícios", diga "eu sou uma pessoa ativa". A identidade molda o comportamento.
                    </p>
                  </div>
                </div>
              </div>

              {/* Michael Phelps */}
              <div className="bg-gradient-to-r from-card to-secondary/20 p-8 rounded-2xl border mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-4">Michael Phelps: O Ritual Inabalável</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      O maior nadador olímpico de todos os tempos, com 23 medalhas de ouro, é famoso por seus rituais pré-treino e pré-competição. Phelps seguia exatamente a mesma rotina todas as manhãs, sem exceção.
                    </p>
                    <p className="text-foreground/80 mb-4">
                      Acordar no mesmo horário, comer o mesmo café da manhã, fazer os mesmos alongamentos, ouvir a mesma música. Esse ritual eliminava a necessidade de decidir – o corpo simplesmente entrava no "modo treino" automaticamente.
                    </p>
                    <p className="text-foreground/80">
                      Durante 6 anos, Phelps treinou todos os dias, incluindo Natal, Ano Novo, aniversários. "Enquanto meus competidores tiravam folga, eu estava na piscina ganhando vantagem", disse ele.
                    </p>
                  </div>
                  <div>
                    <div className="bg-secondary/30 p-4 rounded-lg border mb-4">
                      <p className="text-sm font-medium text-foreground italic">
                        "Se você quer ser o melhor, não pode tirar dias de folga. Os outros estão treinando enquanto você descansa."
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">— Michael Phelps</p>
                    </div>
                    <BlogImage 
                      src="/images/blog/exercicios-academia.jpg" 
                      alt="Atleta olímpico treinando, representando disciplina extrema no hábito de exercícios físicos" 
                      className="h-40"
                    />
                    <p className="text-foreground/80 mt-4">
                      <strong>Lição para criar o hábito de exercícios físicos:</strong> Crie um ritual. Antes de treinar, faça sempre as mesmas coisas na mesma ordem. Isso "programa" seu cérebro para entrar no modo exercício automaticamente.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* A Ciência Por Trás */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">A Ciência de Como Criar o Hábito de Exercícios Físicos</h2>

              <p className="text-foreground/80 mb-8">
                Entender a neurociência por trás da formação de hábitos pode ser a diferença entre sucesso e fracasso quando você quer criar o hábito de exercícios. Vamos explorar o que acontece no seu cérebro e como usar isso a seu favor.
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">O Loop do Hábito de Exercícios</h3>
                  <p className="text-foreground/80 mb-4">
                    Todo hábito segue um ciclo de três partes: <strong>Gatilho → Rotina → Recompensa</strong>. Para criar o hábito de exercícios físicos de forma efetiva, você precisa projetar cada parte desse ciclo intencionalmente.
                  </p>
                  <ul className="space-y-3 text-foreground/80">
                    <li>• <strong>Gatilho:</strong> O alarme às 6h, a roupa de treino já separada</li>
                    <li>• <strong>Rotina:</strong> O treino em si (comece pequeno!)</li>
                    <li>• <strong>Recompensa:</strong> O café especial pós-treino, a sensação de conquista</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-4">A Regra dos 66 Dias</h3>
                  <p className="text-foreground/80 mb-4">
                    Pesquisas da University College London mostram que, em média, leva 66 dias para um comportamento se tornar automático. Isso significa que criar o hábito de exercícios requer paciência – você precisa persistir além do ponto onde a motivação inicial desaparece.
                  </p>
                  <p className="text-foreground/80">
                    A boa notícia: falhar um dia não reseta o processo. O importante é não falhar dois dias seguidos. Como dizem: "nunca perca duas vezes".
                  </p>
                </div>
              </div>

              <div className="bg-primary/10 p-6 rounded-xl border-l-4 border-primary mb-8">
                <h3 className="font-bold text-foreground mb-2">💡 A Dopamina é Sua Aliada</h3>
                <p className="text-foreground/80">
                  O exercício libera dopamina naturalmente. Isso significa que, quanto mais você se exercita, mais seu cérebro começa a "desejar" o exercício. As primeiras semanas são as mais difíceis – depois, seu cérebro começa a trabalhar a seu favor para manter o hábito de exercícios físicos.
                </p>
              </div>
            </section>

            {/* Estratégias Práticas */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">7 Estratégias Comprovadas Para Criar o Hábito de Exercícios</h2>

              <p className="text-foreground/80 mb-8">
                Baseado em pesquisas científicas e na experiência de atletas de alto desempenho, aqui estão as estratégias mais eficazes para criar e manter o hábito de exercícios físicos.
              </p>

              {/* Estratégia 1 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">1</span>
                  Comece Ridiculamente Pequeno
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-foreground/80 mb-4">
                      O maior erro de quem quer criar o hábito de exercícios é começar com intensidade alta demais. Você está motivado, treina uma hora, fica dolorido por uma semana, e desiste. Em vez disso, comece tão pequeno que seja impossível falhar.
                    </p>
                    <p className="text-foreground/80">
                      <strong>Exemplos práticos:</strong> 5 minutos de caminhada, 10 agachamentos, 1 volta no quarteirão. Parece pouco? Ótimo! O objetivo inicial não é fitness – é criar o hábito de exercícios físicos.
                    </p>
                  </div>
                  <div className="bg-card p-4 rounded-xl border">
                    <p className="text-sm text-muted-foreground mb-2">Maria aplicou:</p>
                    <p className="text-foreground/80">"Eu comecei com 10 minutos de caminhada por dia. Parecia ridículo, mas era algo que eu conseguia fazer mesmo nos piores dias. Depois de um mês, meu corpo pedia mais."</p>
                  </div>
                </div>
              </div>

              {/* Estratégia 2 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">2</span>
                  Vincule a um Hábito Existente (Habit Stacking)
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-card p-4 rounded-xl border">
                    <p className="text-sm text-muted-foreground mb-2">Fórmula:</p>
                    <p className="text-foreground font-semibold">"Depois de [HÁBITO ATUAL], eu vou [NOVO HÁBITO DE EXERCÍCIO]."</p>
                    <ul className="mt-4 space-y-2 text-sm text-foreground/80">
                      <li>• Depois de escovar os dentes de manhã, faço 10 agachamentos</li>
                      <li>• Depois de tomar café, caminho 15 minutos</li>
                      <li>• Depois de chegar do trabalho, faço 20 minutos de exercício</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-foreground/80 mb-4">
                      Vincular o novo hábito de exercícios a algo que você já faz automaticamente aumenta drasticamente as chances de sucesso. Seu cérebro usa o hábito existente como "gatilho" para o novo comportamento.
                    </p>
                    <p className="text-foreground/80">
                      Essa técnica, chamada de "habit stacking" (empilhamento de hábitos), foi popularizada por James Clear no livro Atomic Habits e é uma das formas mais eficazes de criar o hábito de exercícios físicos.
                    </p>
                  </div>
                </div>
              </div>

              {/* Estratégia 3 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">3</span>
                  Prepare o Ambiente
                </h3>
                <p className="text-foreground/80 mb-4">
                  Torne o exercício o caminho de menor resistência. Deixe a roupa de treino já separada na noite anterior. Coloque o tênis ao lado da cama. Tenha os equipamentos visíveis e acessíveis.
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-accent/10 p-4 rounded-lg border text-center">
                    <p className="text-3xl mb-2">👟</p>
                    <p className="text-sm text-foreground/80">Tênis ao lado da cama</p>
                  </div>
                  <div className="bg-accent/10 p-4 rounded-lg border text-center">
                    <p className="text-3xl mb-2">🎽</p>
                    <p className="text-sm text-foreground/80">Roupa já separada</p>
                  </div>
                  <div className="bg-accent/10 p-4 rounded-lg border text-center">
                    <p className="text-3xl mb-2">💧</p>
                    <p className="text-sm text-foreground/80">Garrafa de água pronta</p>
                  </div>
                </div>
              </div>

              {/* Estratégia 4 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">4</span>
                  Use a Regra dos 2 Minutos
                </h3>
                <p className="text-foreground/80 mb-4">
                  Quando não estiver com vontade de treinar, comprometa-se a fazer apenas 2 minutos. Coloque a roupa de treino. Faça um aquecimento rápido. Na maioria das vezes, uma vez que você começa, continua.
                </p>
                <p className="text-foreground/80">
                  O segredo para criar o hábito de exercícios é <strong>apenas aparecer</strong>. Mesmo que você faça apenas 5 minutos em um dia ruim, você está reforçando a identidade de alguém que se exercita regularmente.
                </p>
              </div>

              {/* Estratégia 5 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">5</span>
                  Encontre Seu Tipo de Exercício
                </h3>
                <p className="text-foreground/80 mb-4">
                  Você não precisa fazer musculação se odeia academia. Não precisa correr se detesta corrida. O melhor exercício é aquele que você realmente vai fazer. Experimente diferentes modalidades até encontrar algo que você genuinamente goste.
                </p>
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-card p-4 rounded-xl border text-center">
                    <p className="text-2xl mb-2">🏊</p>
                    <p className="text-sm font-medium">Natação</p>
                  </div>
                  <div className="bg-card p-4 rounded-xl border text-center">
                    <p className="text-2xl mb-2">🚴</p>
                    <p className="text-sm font-medium">Ciclismo</p>
                  </div>
                  <div className="bg-card p-4 rounded-xl border text-center">
                    <p className="text-2xl mb-2">🧘</p>
                    <p className="text-sm font-medium">Yoga</p>
                  </div>
                  <div className="bg-card p-4 rounded-xl border text-center">
                    <p className="text-2xl mb-2">💃</p>
                    <p className="text-sm font-medium">Dança</p>
                  </div>
                </div>
              </div>

              {/* Estratégia 6 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">6</span>
                  Rastreie Seu Progresso
                </h3>
                <p className="text-foreground/80 mb-4">
                  O que é medido, melhora. Use um aplicativo, um calendário na parede, ou o HabitFrog para marcar cada dia que você se exercitou. Ver a sequência de dias completados cria motivação para não "quebrar a corrente".
                </p>
                <p className="text-foreground/80">
                  A visualização do progresso é poderosa psicologicamente. Cada dia marcado reforça sua identidade como alguém que tem o hábito de exercícios físicos.
                </p>
              </div>

              {/* Estratégia 7 */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-4">
                  <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-bold">7</span>
                  Tenha um Parceiro ou Comunidade
                </h3>
                <p className="text-foreground/80 mb-4">
                  Estudos mostram que pessoas que têm parceiros de treino ou fazem parte de comunidades fitness têm 65% mais chances de manter o hábito de exercícios. A accountability social é um motivador poderoso.
                </p>
                <p className="text-foreground/80">
                  <strong>Mas atenção:</strong> como Maria aprendeu, não dependa 100% de outra pessoa. Tenha seu compromisso individual primeiro – o parceiro é um bônus, não a fundação.
                </p>
              </div>
            </section>

            <div className="my-16"><CTACard variant="secondary" /></div>

            {/* A Mobilidade Recuperada */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">A Mobilidade Recuperada: O Que Maria Sente Hoje</h2>

              <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
                <BlogImage 
                  src="/images/blog/treino-casa.jpg" 
                  alt="Mulher jovem fazendo exercícios de mobilidade ao ar livre, representando recuperação do hábito de exercícios físicos" 
                  className="h-64"
                />
                <div>
                  <p className="text-foreground/80 mb-4">
                    Seis meses depois de recomeçar sua jornada, Maria compartilha as mudanças que sentiu ao criar o hábito de exercícios novamente:
                  </p>
                  <ul className="space-y-3 text-foreground/80">
                    <li>✅ <strong>Mobilidade articular:</strong> "Consigo agachar completamente, algo que não fazia há 2 anos"</li>
                    <li>✅ <strong>Dores nas costas:</strong> "Sumiram completamente após 3 meses"</li>
                    <li>✅ <strong>Energia:</strong> "Tenho disposição o dia inteiro, não preciso mais de café às 15h"</li>
                    <li>✅ <strong>Sono:</strong> "Durmo profundamente e acordo renovada"</li>
                    <li>✅ <strong>Autoestima:</strong> "Me sinto forte, capaz, no controle da minha vida"</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-golden/20 to-golden/5 p-8 rounded-2xl border border-golden/30 mb-8">
                <div className="flex items-start gap-4">
                  <Quote className="w-8 h-8 text-golden flex-shrink-0 mt-1" />
                  <p className="text-lg text-foreground italic">
                    "O mais louco é que eu achava que precisava de uma hora de treino pesado para ter resultados. Comecei com 10 minutos de caminhada. Hoje faço 45 minutos de treino funcional, mas o hábito foi construído tijolo por tijolo. Se eu tivesse tentado fazer muito no começo, teria desistido de novo. Criar o hábito de exercícios físicos é sobre consistência, não intensidade."
                    <span className="block mt-2 text-muted-foreground not-italic">— Maria, 24 anos</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Erros Comuns */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">5 Erros Que Impedem Você de Criar o Hábito de Exercícios</h2>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral">
                  <h3 className="font-bold text-foreground mb-2">❌ Erro 1: Começar Muito Intenso</h3>
                  <p className="text-foreground/80">
                    Treinar por uma hora no primeiro dia gera dor, cansaço e desistência. Comece com 10-15 minutos e aumente gradualmente.
                  </p>
                </div>
                <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral">
                  <h3 className="font-bold text-foreground mb-2">❌ Erro 2: Depender da Motivação</h3>
                  <p className="text-foreground/80">
                    Motivação é passageira. Para criar o hábito de exercícios, você precisa de sistemas e rotinas, não de inspiração constante.
                  </p>
                </div>
                <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral">
                  <h3 className="font-bold text-foreground mb-2">❌ Erro 3: Não Ter Horário Fixo</h3>
                  <p className="text-foreground/80">
                    "Vou treinar quando der" significa que não vai treinar. Defina um horário específico e proteja esse tempo.
                  </p>
                </div>
                <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral">
                  <h3 className="font-bold text-foreground mb-2">❌ Erro 4: Focar Só em Resultados Estéticos</h3>
                  <p className="text-foreground/80">
                    Mudanças físicas demoram. Se você só se exercita para emagrecer, vai desistir antes de ver resultados. Foque em como se sente.
                  </p>
                </div>
              </div>

              <div className="bg-coral/10 p-6 rounded-xl border-l-4 border-coral">
                <h3 className="font-bold text-foreground mb-2">❌ Erro 5: Depender de Outras Pessoas</h3>
                <p className="text-foreground/80">
                  Como Maria aprendeu, esperar por um parceiro de treino pode sabotar completamente seu hábito de exercícios físicos. Tenha seu compromisso individual primeiro.
                </p>
              </div>
            </section>

            {/* Conclusão */}
            <section className="mb-16">
              <h2 className="text-3xl font-bold text-foreground mb-6">Conclusão: Sua Jornada Para Criar o Hábito de Exercícios Começa Agora</h2>
              
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <p className="text-foreground/80 mb-4">
                    Criar o hábito de exercícios físicos não é sobre encontrar a dieta perfeita, o treino ideal ou o momento certo. É sobre começar – mesmo que pequeno, mesmo que imperfeito, mesmo que hoje você só consiga 5 minutos.
                  </p>
                  <p className="text-foreground/80 mb-4">
                    Maria perdeu anos esperando pelo marido. Atletas de elite como Arnold, Ronaldo e Phelps nos mostram que consistência supera intensidade. A ciência comprova que seu cérebro pode ser reprogramado para desejar o exercício.
                  </p>
                  <p className="text-foreground/80">
                    A única coisa que separa quem consegue de quem não consegue é a decisão de começar – e de não parar.
                  </p>
                </div>
                <BlogImage 
                  src="/images/blog/corrida-atleta.jpg" 
                  alt="Pessoa sorrindo ao completar treino ao nascer do sol, representando sucesso em criar o hábito de exercícios físicos" 
                  className="h-64"
                />
              </div>

              <div className="bg-gradient-to-r from-primary/20 via-accent/20 to-secondary/20 p-8 rounded-2xl border mb-8">
                <p className="text-xl text-foreground text-center mb-4">
                  <strong>Seu próximo passo:</strong>
                </p>
                <p className="text-2xl font-bold text-primary text-center">
                  Escolha um horário. Defina 10 minutos. Comece amanhã. Não espere mais por ninguém.
                </p>
              </div>

              <p className="text-xl font-semibold text-primary text-center py-4">
                Você tem tudo que precisa para criar o hábito de exercícios físicos. A única pergunta é: você vai começar hoje?
              </p>
            </section>

            <CTACard variant="primary" />
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HabitoExercicios;
